import type { IconProp } from '@fortawesome/fontawesome-svg-core';

// helper
type KeyWithValueType<D, V> = {
  [K in keyof D]: D[K] extends V ? K : never;
}[keyof D];
type AllowedIdType = string | number;
export type AllowedIdField<D> = NonNullable<KeyWithValueType<D, AllowedIdType>>;

enum EnumTableFieldType {
  String = 1,
  Select = 2,
  Checkbox = 3,
  CheckboxAdvanced = 3001, // new type as experimental
  Date = 4,
  Integer = 5,
  Color = 6,
  Image = 7,
  Time = 8,
  DateTime = 9,
  MultiValue = 10,
  Percent = 11,
}

enum EnumTableSortDirection {
  None = 0,
  Ascending = 1,
  Descending = 2,
}

type Identifier = string | number;

type Option = {
  Key: Identifier;
  Value: Identifier;
};

interface TableColumn {
  Label: string | React.ReactElement;
  FieldName: string;
  FieldType?: EnumTableFieldType;
  Sortable?: boolean;
  Searchable?: boolean;
  Filterable?: boolean;
  Editable?: boolean;
  ReadOnly?: boolean;
  /** Row field name: when set, cell ReadOnly is Row[CellReadOnly] (per-row readonly) */
  CellReadOnly?: string;
  /** Row field name: for a CheckboxAdvanced column, the checkbox is disabled when Row[CellDisabled] is truthy (per-row disable) */
  CellDisabled?: string;
  Visible?: boolean;
  Width?: string;
  MinWidth?: string;
  Alignment?: number;
  Placeholder?: string;
  ShowSearch?: boolean;
  Options?: Option[];
  SelectedOptions?: Identifier[];
  RowOptions?: string;
  Success?: boolean;
  RenderLabel?: (props: { ColumnIndex: number; Label: string }) => React.ReactNode;
  RenderRow?: (props: { ColumnIndex: number; Row: any; Column: any }) => React.ReactNode;
}

type BasicRecord = Record<string, any>;

interface InitialTableState<D extends BasicRecord> {
  InitialSortField?: keyof D;
  InitialSortDirection?: EnumTableSortDirection;
  InitialColumns?: (keyof D)[];
  InitialFilterValues?: Record<string, string[]>;
  InitialSearch?: string;
  InitialColumnWidths?: Record<string, number>;
  InitialColumnOrder?: string[];
}

interface SCAdvanceTableSelectProps {
  Selectable?: boolean;
  Selected?: any[];
  OnSelect?: (selected: any[]) => void;
}

interface SCAdvanceTableTreeProps {
  Expandable?: boolean;
  // default is 'alone'
  ExpandColumn?: 'alone' | 'first';
  DefaultExpandAll?: boolean;
  Expanded?: any[];
  OnExpanded?: (expanded: any[]) => void;
}

interface SCAdvanceTableProps<D extends BasicRecord>
  extends SCAdvanceTableSelectProps, SCAdvanceTableTreeProps {
  Title?: string;
  Icon?: IconProp;
  DataTestId?: string;

  Id?: string;
  InitialTableState?: InitialTableState<D>;

  Columns: TableColumn[];
  Rows?: D[];
  Loading?: boolean;
  Success?: boolean;
  IdField: AllowedIdField<D>;
  RowButtons?: {
    Title: string;
    Icon?: IconProp;
    HoverIcon?: IconProp;
    Cancel?: boolean;
    OnClick?: (id: string, row: D, index?: number) => void;
    Disabled?: boolean;
    Hidden?: string | boolean;
    MenuItems?: {
      Label?: string;
      Icon?: IconProp;
      OnClick?: (id: string, row: D) => void;
      Disabled?: boolean;
      Divider?: boolean;
      Visible?: boolean;
    }[];
  }[];
  FixedRowButtons?: boolean;
  /** Collapse per-row hidden RowButtons entirely instead of rendering a
   *  fixed-width spacer, so mutually-exclusive buttons (e.g. Run/Register)
   *  share one slot. Forwarded to eone-ui's TablePanel. */
  CollapseHiddenRowButtons?: boolean;
  /** Row set used to size the row-button column, independent of the (possibly
   *  filtered) `Rows` actually rendered. Provide the unfiltered rows here when
   *  `Rows` is client-side filtered so the action column width stays stable and
   *  doesn't reflow as rows are hidden/shown (e.g. an active/inactive toggle).
   *  Falls back to `Rows` when omitted. */
  RowsForButtonCount?: D[];
  MaxHeightWithBreadcrumb?: boolean;
  EmptyIcon?: any;
  EmptyTitle?: string;
  /** Buttons rendered in the panel title bar (above the table). */
  HeaderButtons?: {
    Label: string;
    Icon?: IconProp;
    OnClick?: () => void;
    Visible?: boolean;
    Disabled?: boolean;
    DataTestId?: string;
    /** Read by eone-ui's PanelHeader on the default render path. */
    Primary?: boolean;
    Tertiary?: boolean;
    /** Visual hierarchy type. When set on any button, SmartConnect renders
     *  all header buttons itself (bypassing eone-ui's PanelHeader which only
     *  supports primary/secondary). */
    Type?: 'primary' | 'secondary' | 'tertiary';
    MenuItems?: {
      Label: string;
      Icon?: IconProp;
      OnClick?: (data?: any) => void;
      Visible?: boolean;
      Divider?: boolean;
      Danger?: boolean;
      DataTestId?: string;
    }[];
  }[];
  /** Buttons rendered in the table container header (same row as the search box). */
  TableHeaderButtons?: {
    Label: string;
    Icon?: IconProp;
    OnClick?: () => void;
    Visible?: boolean;
    MenuItems?: {
      Label: string;
      Icon?: IconProp;
      OnClick?: (data?: any) => void;
      Visible?: boolean;
      Divider?: boolean;
      Danger?: boolean;
    }[];
  }[];
  PersistSortAndFilter?: boolean;
  /** Text, or an element (e.g. one carrying a data-testid) - rendered as given. */
  EmptyMessage?: React.ReactNode;
  /** With no rows, draw the column headers above the empty state instead of the empty state alone. */
  ShowHeaderRowWhenEmpty?: boolean;
  EmptyPrompts?: {
    Label: string;
    OnClick: () => void;
  }[];
  Search?: boolean;
  ShowSelectColumns?: boolean;
  TableState?: any;
  BodyHeight?: string;
  StickyHeader?: boolean;
  HorizontalScroll?: boolean;

  // ChangeValue?: (idField: string, field: string, value: any) => void;
  ChangeValue?: (id: string, field: keyof D, value: any) => void;

  /** Custom sort function. When provided, overrides the built-in sort logic in eone-ui. */
  CustomSort?: (rows: D[], fieldName: string, direction: number, sortColumn: any) => D[];

  /** Row icon indicators displayed alongside cell content. */
  RowIcons?: any[];

  ReadOnly?: boolean;
  Reorderable?: boolean;
  Reorder?: (id: string, startIndex: number, endIndex: number) => void;

  SelectedRowId?: D[keyof D];

  /** Side-by-side details panel feature (matches TablePanel props). */
  AllowDetailsPanel?: boolean;
  /** Show column filters regardless of the saved column state. Forwarded to eone-ui TablePanel. */
  ShowFilterAlways?: boolean;
  /** Use eone-ui's select-style filter menus. Forwarded to eone-ui TablePanel. */
  FilterSelectUI?: boolean;
  /** Enables the Grid/Card view-mode toggle in the panel header. */
  AllowViewSwitching?: boolean;
  /** Render function for the details panel content for the selected row.
   *  Method syntax so a callback typed for the actual id type (e.g. string) fits. */
  DetailsPanel?(id: D[keyof D] | undefined, row: D | undefined): React.ReactNode;
  DetailsPanelTitle?: string;
  /** Callback invoked when the user selects a row (used for the details panel).
   *  Method syntax, as for DetailsPanel. */
  SelectRow?(id: D[keyof D]): void;
  /** From eone-ui/tables/table-constants; kept loose to avoid pulling the enum import here. */
  CardIconType?: number | string;
  /**
   * Render a custom component for each card in Grid/Card view instead of the
   * default eone-ui card. Receives the same props the built-in TableCard does:
   * Row, Columns, IdField, SelectedRowId, OnSelect, OnDoubleClick, RowButtons,
   * CardIconType, Locale, Reorderable, DataTestId.
   */
  CustomCard?: React.ComponentType<any>;

  // below for eone-ui sc branch only
  TableExtraProps?: Partial<EoneTableProps>;
}
interface EoneTableProps {
  // add more if needed
  // below for eone-ui sc branch only
  RowExtraProps?: (id: AllowedIdType, index: number) => Partial<EoneTableRowProps> | undefined;
  ShownOptions: { Key: any; Value: any }[];
}
interface EoneTableRowProps {
  // add more if needed
  // below for eone-ui sc branch only
  Class?: string;
}

interface SCAdvanceTableInteractiveProps<D extends BasicRecord> extends SCAdvanceTableProps<D> {
  /** Enable column resize with width persistence. Default: true */
  ColumnResizable?: boolean;
  /** Enable column reorder via manage columns modal. Default: true */
  ColumnReorderable?: boolean;
}

export { EnumTableFieldType, EnumTableSortDirection };
export type {
  BasicRecord,
  InitialTableState,
  SCAdvanceTableInteractiveProps,
  SCAdvanceTableProps,
  TableColumn,
};
